import { tfsToDate, isTFS } from './TimecodeUtils';
import MessageService from 'message-service/message-service';
import DataEvents from '../../../events/DataEvents';
import DistEvents from '../../../events/DistEvents';
import { socket, noDelaySocket } from '../../../datasources/socket';
import { defaults as defaultsDatasource } from '../../../datasources/defaults';
import datasourceManager from 'datasource-manager';

const { NEW_TIMECODE, TK_SOCKET_INITIATED_FAILOVER } = DataEvents;
const { ACCESS_TOKEN_CHANGE, ACCESS_TOKEN_READY } = DistEvents;

// Stand-alone source stack just got managing the socket
const socketSourceManager = datasourceManager.create();

const SOCKET = 'socket';
const NODELAY_SOCKET = 'noDelaySocket';

// Add socket, no-delay and defaults
socketSourceManager.add({
    defaults: defaultsDatasource,
    [SOCKET]: socket,
    [NODELAY_SOCKET]: noDelaySocket
});

const responseCodePreventReconnect = new Set([4200, 4256, 4400, 4401, 4404, 4301, 4429]);

const initSocketState = {
    socket: null,
    pushUpdateId: null,
    isLive: false,
    isValidStartTime: false,
    isValidGameState: null,
    connectionAttempts: 6, // initial connection + 5 retry attempts
    game: null,
    config: null,
    timeMarchesOn: false,
    intervalId: -1
};

class SocketSource {
    constructor (opts) {
        this.config = opts.config;
        this.socket = initSocketState.socket;
        this.pushUpdateId = initSocketState.pushUpdateId;
        this.isLive = initSocketState.isLive;
        this.isValidStartTime = initSocketState.isValidStartTime;
        this.connectionAttempts = initSocketState.connectionAttempts;
        this.isValidGameState = initSocketState.isValidGameState;
        this.timeMarchesOn = false; // this is just a flag so we should keep stayinAlive
        this.intervalId = -1;
        this.firstRun = true; // a flag so we don't rerun stayinAlive every time we open the socket
        this.socketSourceManager = socketSourceManager;
        this.socketSourceManager.setEnv(this.config?.websocketConfig?.env);
    }

    static authAccessToken = null;
    static tokenReady = true;

    start () {
        this.timeMarchesOn = true;

        MessageService.on(ACCESS_TOKEN_CHANGE, (token) => {
            this.setAccessToken(token);
        });

        MessageService.on(ACCESS_TOKEN_READY, (token) => {
            this.setAccessToken(token);
            this.setUpSocket();
            SocketSource.tokenReady = false;
        });

        if (!SocketSource.tokenReady) {
            this.setUpSocket();
        }
    }

    setUpSocket () {
        const { config } = this;
        const socketSource = config.websocketConfig?.noDelayWebSockets ? NODELAY_SOCKET : SOCKET;
        const socketURL = this.socketSourceManager.get(socketSource).getUrl(config.gameInfo);
        const protocol = SocketSource.authAccessToken ? ['gameday', SocketSource.authAccessToken] : undefined;
        // Open Sesame!
        this.socket = !protocol ? new WebSocket(socketURL) : new WebSocket(socketURL, protocol);

        // set up the socket
        this.socket.onclose = this.onSocketClose.bind(this);
        this.socket.onmessage = this.onSocketMessage.bind(this);
        this.socket.onerror =  this.onSocketError.bind(this);
        this.socket.onopen = this.onSocketOpen.bind(this);
    }

    onSocketMessage (e) {
        // data is coming to us as one big string, needs to be parsed
        const msg = JSON.parse(e.data);
        if (msg && msg.updateId) {
            this.handleMessage(msg);
        }
    }

    onSocketOpen () {
        if (this.firstRun === true) {
            this.firstRun = false;
            this.stayinAlive();
        }
    }

    onSocketClose (e) {
        this.connectionAttempts = this.connectionAttempts - 1;
        if (responseCodePreventReconnect.has(e?.code) || this.connectionAttempts == 0) {
            this.soundTheAlarms();
        }
    }

    onSocketError (e) {
        // close socket, set to null
        this.soundTheAlarms();
    }

    handleMessage (msg) {
        const pushUpdateId = msg.updateId;
        const timecode = isTFS(msg.timeStamp) ? tfsToDate(msg.timeStamp) : msg.timeStamp;
        MessageService.publish(NEW_TIMECODE, ({timecode, pushUpdateId}));
    }

    stop () {
        !!this?.socket && this.socket.close();
        this.timeMarchesOn = false;
        clearTimeout(this.intervalId);
        this.socket = null;
    }

    stayinAlive () {
        if (this.timeMarchesOn == true) {
            const socket = this.socket;
            const readyState = socket?.readyState;

            if (readyState === socket.OPEN || readyState === socket.CONNECTING) { // if the socket is alive, keep keeping it alive
                try {
                    socket && socket.send('Gameday');
                    // We have a positive socket connection
                    // so reset the connection attempt counter to the retry (minus initial).
                    this.connectionAttempts = initSocketState.connectionAttempts - 1;
                } catch (socketError) {
                    // If we've encountered an error during the send, then something is wrong.
                    // Close the socket if we can, and lets try again.
                    socket && socket.close();
                }
            } else if (!readyState) {
                // Not able to read a ready state from the socket
                this.soundTheAlarms();
            } else {
                if (readyState === socket.CLOSING) {
                    this.onSocketClose({}); // if its dying, suffocate it
                } else if (this.connectionAttempts > 0) { // if its dead but not out of lives, its frankenstein time
                    // post up a fake time code to keep things moving while we try to resuscitate the socket
                    const timecode = new Date();
                    MessageService.publish(NEW_TIMECODE, ({timecode}));
                    this.setUpSocket();
                }
            }

            this.intervalId = setTimeout(() => {
                this.stayinAlive(); // ~recursion~
            }, 10000);
        }
    }

    setAccessToken (accessToken) {
        const socket = this?.socket;
        if (accessToken !== SocketSource.authAccessToken) {
            SocketSource.authAccessToken = accessToken; // set access token
        }

        if (socket && socket.readyState === socket.OPEN) {
            socket.close(); // close only when the socket is fully open
        }
    }

    soundTheAlarms () {
        this.timeMarchesOn = false;
        clearTimeout(this.intervalId);
        MessageService.publish(TK_SOCKET_INITIATED_FAILOVER);
    }
}

export default SocketSource;